import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types';
import {
  Choice,
  INITIAL_CHOICE,
  INITIAL_INTERESTED_PRODUCT_LINE,
  InterestedProductLine,
  ProductInfo,
  RateStatistic,
  Subscriber,
} from 'types/electionService';

const createInitialInterestedProductLinesList = (
  subscriber: Subscriber,
  productLineIds: ProductLineId[],
): InterestedProductLine[] => {
  const defaultDependentsList: string[] = subscriber.custom.dependents.map(({ id }) => id);

  if (productLineIds.length === 0) {
    return [];
  }

  const defaultInitialInterestedProductLinesList: InterestedProductLine[] = productLineIds.map((productLineId) => {
    return {
      ...INITIAL_INTERESTED_PRODUCT_LINE,
      productLineId,
      dependentIds: defaultDependentsList,
    };
  });
  return defaultInitialInterestedProductLinesList;
};

/**
 * Builds initial product line interest for a subscriber by merging
 * domain default product lines with the subscriber’s selections.
 *
 * @param subscriber - The subscriber to build product line interest for.
 * @param productLineIds - Product line IDs from the domain.
 * @param rateStats - Used to determine default dependent selections.
 * @returns Initial product line interest for the subscriber across available product lines.
 */
export const buildInterestedProductLinesForSubscriber = (
  subscriber: Subscriber,
  productLineIds: ProductLineId[],
): InterestedProductLine[] => {
  const initialInterestedProductLinesList = createInitialInterestedProductLinesList(subscriber, productLineIds);
  const populatedInterestedProductLines = subscriber.interestedProductLines ?? [];

  const interestedProductLinesByProductLine = new Map(
    populatedInterestedProductLines.map((interestedProductLine) => [
      interestedProductLine.productLineId,
      interestedProductLine,
    ]),
  );

  return initialInterestedProductLinesList.map((initialInterestedProductLine) => {
    const existing = interestedProductLinesByProductLine.get(initialInterestedProductLine.productLineId);
    if (!existing) return initialInterestedProductLine;

    return {
      ...initialInterestedProductLine,
      value: existing.value,
      dependentIds: existing.dependentIds,
    };
  });
};

const createInitialChoicesList = (
  subscriber: Subscriber,
  products: ProductInfo[],
  rateStats: RateStatistic | null,
): Choice[] => {
  const defaultDependentsList: string[] = subscriber.custom.dependents.map(({ id }) => id);

  if (products.length === 0) {
    return [];
  }

  const defaultInitialChoicesList: Choice[] = products.map(({ productLineId }) => {
    // productLine with 1 tier needs dependentIds as empty array
    const hasProductLineOneTier = rateStats ? Object.keys(rateStats?.[productLineId] || []).length === 1 : false;
    return { ...INITIAL_CHOICE, productLineId, dependentIds: hasProductLineOneTier ? [] : defaultDependentsList };
  });
  return defaultInitialChoicesList;
};

/**
 * Builds initial choices for a subscriber by merging
 * default product lines from available products with the subscriber’s selections.
 *
 * @param subscriber - The subscriber to build choices for.
 * @param products - Available products that define the product line list.
 * @param rateStats - Used to determine default dependent selections for product lines.
 * @returns Initial choices for the subscriber across product lines from available products.
 */
export const buildChoicesForSubscriber = (
  subscriber: Subscriber,
  products: ProductInfo[],
  rateStats: RateStatistic | null,
): Choice[] => {
  const initialChoices = createInitialChoicesList(subscriber, products, rateStats);
  const populatedChoices = subscriber.choice ?? [];

  const choicesByProductLine = new Map(populatedChoices.map((choice) => [choice.productLineId, choice]));

  return initialChoices.map((initialChoice) => ({
    ...initialChoice,
    ...choicesByProductLine.get(initialChoice.productLineId),
  }));
};
